import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import PostAction from "../store/post/action";
import AppAction from "../store/app/action";
import FavoritesActions from "../store/favorites/action";
import LoadingView from "../components/LoadingView";
import Clothes from "../models/clothes/clothes.model";
import { FaHeart, FaRegHeart, FaArrowLeft, FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { categoryLabels } from "../utils/categoryData";
import { colorLabels, colorHex, genderOptions } from "../utils/filterOptions";
import type { ClothesColors } from "../models/clothes/clothes.model";
import type { AppTransport } from "../models/app/app.model";

const subtypeLabels: Record<string, string> = {
    long: "E gjatë",
    short: "E shkurtër",
    medium: "Mesatare",
};

const ClothesDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const item = location.state?.item as Clothes | undefined;
    const { user } = useAppSelector((s) => s.auth);
    const { favorites } = useAppSelector((s) => s.favorites);
    const { seller, sellersPosts, isLoading } = useAppSelector((s) => s.post);
    const { data: appData } = useAppSelector((s) => s.app);

    const isFavorite = item && FavoritesActions.isFavorite(item, favorites);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showLoginToContactModal, setShowLoginToContactModal] = useState(false);
    const images = item?.images ?? [];
    const hasMultipleImages = images.length > 1;

    useEffect(() => {
        setSelectedImageIndex(0);
    }, [item?.id]);

    useEffect(() => {
        AppAction.getAppData(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (item?.seller_id) {
            PostAction.getSellerInfo(item.seller_id, dispatch);
            PostAction.getSellersPosts(item.seller_id, dispatch);
        }
        return () => {
            PostAction.clearSeller(dispatch);
        };
    }, [item?.seller_id, dispatch]);

    const transportList: AppTransport[] = appData?.transport?.length
        ? appData.transport
        : [
            { country: "Kosovë", country_code: "XK", fee: 2 },
            { country: "Maqedoni", country_code: "MK", fee: 3 },
            { country: "Shqipëri", country_code: "AL", fee: 5 },
        ];

    const getTransportDisplay = (t: AppTransport) => {
        if (t.country_code === "XK" && item?.isTransportXKIncluded) return "Falas";
        if (t.country_code === "MK" && item?.isTransportMKIncluded) return "Falas";
        if (t.country_code === "AL" && item?.isTransportALIncluded) return "Falas";
        return `${t.fee} €`;
    };

    const toggleFavorite = () => {
        if (!item) return;
        if (isFavorite) FavoritesActions.removeItem(item, favorites, dispatch);
        else FavoritesActions.addItem(item, favorites, dispatch);
    };

    const goToSeller = () => {
        if (item?.seller_id) navigate("/seller/" + item.seller_id, { state: { seller, sellersPosts } });
    };

    const goToOrder = () => {
        if (!user) {
            navigate("/", { state: { returnUrl: "/order/create", returnState: { item } } });
            return;
        }
        navigate("/order/create", { state: { item } });
    };

    const onContactClick = (link: string) => {
        if (!user) {
            setShowLoginToContactModal(true);
            return;
        }
        if (link.startsWith("wa:")) {
            const num = link.slice(3).replace(/\D/g, "");
            window.open(`https://wa.me/${num}`, "_blank");
        } else {
            window.open(link, "_blank");
        }
    };

    if (!item) {
        return (
            <div className="min-h-screen bg-[#f8f2ed]">
                <Navbar />
                <div className="p-4">Produkti nuk u gjet.</div>
            </div>
        );
    }

    const isOwnPost = user?.id === item.seller_id;

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-4xl mx-auto px-4 py-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-[#704e37] mb-4"
                >
                    <FaArrowLeft className="mr-2" /> Prapa
                </button>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col md:flex-row">
                    <div className="md:w-1/2 flex flex-col">
                        <div className="aspect-[3/4] bg-gray-100">
                            {images[selectedImageIndex] ? (
                                <img
                                    src={images[selectedImageIndex]}
                                    alt={`${item.name} - ${selectedImageIndex + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
                            )}
                        </div>
                        {hasMultipleImages && (
                            <div className="flex gap-2 p-2 bg-gray-50 border-t">
                                {images.map((src, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setSelectedImageIndex(index)}
                                        className={`flex-1 aspect-square rounded-lg overflow-hidden border-2 transition shrink-0 ${selectedImageIndex === index ? "border-[#704e37] ring-1 ring-[#704e37]" : "border-transparent opacity-70 hover:opacity-100"}`}
                                    >
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="md:w-1/2 p-6 flex flex-col">
                        <div className="flex justify-between items-start">
                            <h1 className="text-2xl font-semibold text-gray-800">{item.name}</h1>
                            <button
                                onClick={toggleFavorite}
                                className="p-2 rounded-full hover:bg-gray-100"
                                aria-label="Favorites"
                            >
                                {isFavorite ? (
                                    <FaHeart className="text-xl" color="#ed1b23" />
                                ) : (
                                    <FaRegHeart className="text-xl" color="#aaa" />
                                )}
                            </button>
                        </div>
                        {item.description && (
                            <p className="text-gray-600 mt-2 text-sm leading-relaxed">{item.description}</p>
                        )}

                        <div className="mt-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                    <span className="text-gray-500 block text-xs font-medium uppercase tracking-wide">Kategoria</span>
                                    <span className="text-gray-800 font-medium">{item.type ? categoryLabels[item.type] : "—"}</span>
                                </div>
                                {item.subtype && (
                                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                                        <span className="text-gray-500 block text-xs font-medium uppercase tracking-wide">Nënshtypi</span>
                                        <span className="text-gray-800 font-medium">{subtypeLabels[item.subtype] ?? item.subtype}</span>
                                    </div>
                                )}
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                    <span className="text-gray-500 block text-xs font-medium uppercase tracking-wide">Gjinia</span>
                                    <span className="text-gray-800 font-medium">{item.gender ? (genderOptions.find((o) => o.value === item.gender)?.label ?? item.gender) : "—"}</span>
                                </div>
                            </div>

                            {item.size?.length ? (
                                <div>
                                    <span className="text-gray-500 text-xs font-medium uppercase tracking-wide block mb-1.5">Madhësia</span>
                                    <div className="flex flex-wrap gap-2">
                                        {item.size.map((s) => (
                                            <span key={s} className="inline-flex items-center px-3 py-1 rounded-full bg-[#704e37]/10 text-[#704e37] font-medium text-sm">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {item.colors?.length ? (
                                <div>
                                    <span className="text-gray-500 text-xs font-medium uppercase tracking-wide block mb-1.5">Ngjyra</span>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {item.colors.map((c) => (
                                            <span key={c} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 text-sm">
                                                <span
                                                    className="w-4 h-4 rounded-full border border-gray-200 shrink-0"
                                                    style={{ backgroundColor: colorHex[c as ClothesColors] ?? "#ccc" }}
                                                    aria-hidden
                                                />
                                                {colorLabels[c as ClothesColors] ?? c}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <div className="bg-[#704e37]/5 border border-[#704e37]/20 rounded-lg px-4 py-3">
                                <span className="text-gray-500 text-xs font-medium uppercase tracking-wide block mb-0.5">Çmimi</span>
                                {item.prices?.isFree ? (
                                    <span className="text-lg font-semibold text-[#704e37]">Falas</span>
                                ) : (
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-800">
                                        {item.prices?.sellPrice != null && item.prices.sellPrice > 0 && (
                                            <span className="font-semibold">{item.prices.sellPrice} € <span className="text-gray-500 font-normal text-sm">(shitje)</span></span>
                                        )}
                                        {item.prices?.rentPrice != null && item.prices.rentPrice > 0 && (
                                            <span className="font-semibold">{item.prices.rentPrice} € <span className="text-gray-500 font-normal text-sm">(qira)</span></span>
                                        )}
                                        {(!item.prices?.sellPrice || item.prices.sellPrice === 0) && (!item.prices?.rentPrice || item.prices.rentPrice === 0) && !item.prices?.isFree && (
                                            <span className="text-gray-500">—</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* <div>
                                <span className="text-gray-500 text-xs font-medium uppercase tracking-wide block mb-1.5">Transport</span>
                                <div className="flex flex-wrap gap-2">
                                    {transportList.map((t) => {
                                        const isIncluded =
                                            (t.country_code === "XK" && item.isTransportXKIncluded) ||
                                            (t.country_code === "MK" && item.isTransportMKIncluded) ||
                                            (t.country_code === "AL" && item.isTransportALIncluded);
                                        const display = getTransportDisplay(t);
                                        return (
                                            <span
                                                key={t.country_code}
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm ${isIncluded ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}
                                            >
                                                <span>{t.country}:</span>
                                                <span className="font-medium">{display}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div> */}
                        </div>

                        {seller && (
                            <button
                                onClick={goToSeller}
                                className="mt-4 text-[#704e37] font-medium hover:underline"
                            >
                                Shiko shitësin: {seller.first_name} {seller.last_name}
                            </button>
                        )}

                        {seller && !isOwnPost && (
                            (seller.contact?.facebook && seller.verifiedContacts?.facebook === "approved") ||
                            (seller.contact?.instagram && seller.verifiedContacts?.instagram === "approved") ||
                            (seller.contact?.tiktok && seller.verifiedContacts?.tiktok === "approved") ||
                            (seller.contact?.phone && seller.verifiedContacts?.phone === "approved")
                        ) && (
                                <div className="mt-4">
                                    <p className="text-sm font-semibold text-gray-800 mb-2">Kontakti:</p>
                                    <div className="flex flex-wrap gap-3">
                                        {seller.contact?.facebook && seller.verifiedContacts?.facebook === "approved" && (
                                            <button
                                                type="button"
                                                onClick={() => onContactClick(seller.contact!.facebook!)}
                                                className="w-10 h-10 rounded-full bg-[#1877f2]/10 text-[#1877f2] flex items-center justify-center hover:bg-[#1877f2]/20 transition"
                                                aria-label="Facebook"
                                            >
                                                <FaFacebookF className="text-xl" />
                                            </button>
                                        )}
                                        {seller.contact?.instagram && seller.verifiedContacts?.instagram === "approved" && (
                                            <button
                                                type="button"
                                                onClick={() => onContactClick(seller.contact!.instagram!)}
                                                className="w-10 h-10 rounded-full bg-pink-500/10 text-pink-600 flex items-center justify-center hover:bg-pink-500/20 transition"
                                                aria-label="Instagram"
                                            >
                                                <FaInstagram className="text-xl" />
                                            </button>
                                        )}
                                        {seller.contact?.tiktok && seller.verifiedContacts?.tiktok === "approved" && (
                                            <button
                                                type="button"
                                                onClick={() => onContactClick(seller.contact!.tiktok!)}
                                                className="w-10 h-10 rounded-full bg-gray-900/10 text-gray-800 flex items-center justify-center hover:bg-gray-900/20 transition"
                                                aria-label="TikTok"
                                            >
                                                <FaTiktok className="text-xl" />
                                            </button>
                                        )}
                                        {seller.contact?.phone && seller.verifiedContacts?.phone === "approved" && (
                                            <button
                                                type="button"
                                                onClick={() => onContactClick("wa:" + (seller.contact!.phone ?? ""))}
                                                className="w-10 h-10 rounded-full bg-green-500/10 text-green-600 flex items-center justify-center hover:bg-green-500/20 transition"
                                                aria-label="WhatsApp"
                                            >
                                                <FaWhatsapp className="text-xl" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                        <div className="mt-auto pt-6 flex flex-col gap-3">
                            {/* {isOwnPost && (
                                <button
                                    onClick={goToOrder}
                                    className="py-3 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90"
                                >
                                    Krijo porosi
                                </button>
                            )} */}
                            {!isOwnPost && (
                                <p className="text-sm text-gray-600">
                                    Kontaktoni shitësin përmes Facebook, Instagram, TikTok ose WhatsApp për të blerë. Pas marrëveshjes, shitësi krijon porosinë për ju.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {isLoading && <LoadingView size={40} />}

                {showLoginToContactModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowLoginToContactModal(false)}>
                        <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Kyçuni</h3>
                            <p className="text-gray-600 text-sm mb-4">Për të kontaktuar me shitësin rreth produktit, ju duhet të kyçeni.</p>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowLoginToContactModal(false)} className="flex-1 py-2.5 rounded-lg border border-gray-300 font-medium">Kthehuni</button>
                                <button type="button" onClick={() => { setShowLoginToContactModal(false); navigate("/login", { state: { returnUrl: location.pathname, returnState: location.state } }); }} className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90">Kyçuni</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClothesDetail;
